import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { AgentCard } from '../../components/agents/AgentCard';
import { RecentConversations } from '../../components/agents/RecentConversations';
import { AgentComparison } from '../../components/agents/AgentComparison';
import { 
  MessageSquare, 
  Sparkles, 
  BarChart3, 
  Target, 
  Users, 
  PenTool,
  TrendingUp,
  ArrowRight
} from 'lucide-react';

const AGENTS = [
  {
    type: 'data',
    name: 'Analytics Agent',
    description: 'Expert in Google Analytics data analysis and insights',
    icon: BarChart3,
    color: 'blue',
    available: true,
    capabilities: [
      'GA4 data analysis',
      'Traffic analysis', 
      'Conversion tracking',
      'Audience insights',
      'Performance reporting'
    ],
    useCases: [
      'How many users visited yesterday?',
      'What are my top traffic sources?',
      'Show me conversion funnel analysis',
      'Compare this month vs last month'
    ]
  },
  {
    type: 'marketing',
    name: 'Marketing Advisor',
    description: 'Strategic marketing guidance and best practices',
    icon: Target,
    color: 'green',
    available: false,
    capabilities: [
      'Marketing strategy',
      'Campaign optimization',
      'Channel recommendations',
      'Budget allocation',
      'Growth tactics'
    ],
    useCases: [
      'How can I improve my conversion rate?',
      'What marketing channels should I focus on?',
      'Create a marketing strategy for Q2',
      'How to optimize my ad spend?'
    ]
  },
  {
    type: 'strategy',
    name: 'Strategy Consultant',
    description: 'Comprehensive marketing strategy development',
    icon: TrendingUp,
    color: 'purple',
    available: false,
    capabilities: [
      'Growth strategies',
      'Competitive analysis',
      'Market positioning',
      'Strategic planning',
      'Business development'
    ],
    useCases: [
      'Develop a growth strategy',
      'Analyze competitor performance',
      'Plan market expansion',
      'Create strategic roadmap'
    ]
  },
  {
    type: 'persona',
    name: 'Persona Expert',
    description: 'User persona development and audience analysis',
    icon: Users,
    color: 'pink',
    available: false,
    capabilities: [
      'Persona creation',
      'Audience segmentation',
      'User behavior analysis',
      'Customer journey mapping',
      'Targeting optimization'
    ],
    useCases: [
      'Create user personas from my data',
      'Analyze audience segments',
      'Map customer journey',
      'Optimize targeting strategy'
    ]
  },
  {
    type: 'content',
    name: 'Content Specialist',
    description: 'Content marketing and creative guidance',
    icon: PenTool,
    color: 'orange',
    available: false,
    capabilities: [
      'Content strategy',
      'Creative ideas',
      'Content optimization',
      'Editorial planning',
      'SEO content'
    ],
    useCases: [
      'Create content strategy',
      'Generate blog post ideas',
      'Optimize content for SEO',
      'Plan content calendar'
    ]
  }
];

export function AgentsPage() {
  const navigate = useNavigate();
  const { agentType } = useParams();
  const [selectedAgent, setSelectedAgent] = useState(agentType || null);

  const handleStartConversation = (agent: string) => {
    const agentData = AGENTS.find(a => a.type === agent);
    if (agentData?.available) {
      navigate(`/agents/${agent}/conversation`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Marketing Agents</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Choose an AI agent specialized for your marketing needs
          </p>
        </div>
        <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
          <Sparkles className="w-4 h-4 mr-2" />
          Start New Conversation
        </Button>
      </div>

      {/* Agent Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {AGENTS.map((agent) => (
          <AgentCard
            key={agent.type}
            agent={agent}
            isSelected={selectedAgent === agent.type && agent.available}
            onSelect={() => agent.available && setSelectedAgent(agent.type)}
            onStartConversation={() => handleStartConversation(agent.type)}
          />
        ))}
      </div>

      {/* Agent Comparison */}
      {selectedAgent && AGENTS.find(a => a.type === selectedAgent)?.available && (
        <Card className="p-6">
          <AgentComparison 
            agent={AGENTS.find(a => a.type === selectedAgent)!}
            onStartConversation={() => handleStartConversation(selectedAgent)}
          />
        </Card>
      )}

      {/* Recent Conversations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Conversations</h2>
            <Button variant="ghost" size="sm">
              View all
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <RecentConversations />
        </Card>

        {/* Quick Start Guide */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Quick Start Guide</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                <span className="text-xs font-semibold text-blue-600">1</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Choose Your Agent</h4>
                <p className="text-sm text-gray-600">Select the AI agent that matches your current need</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                <span className="text-xs font-semibold text-blue-600">2</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Ask Your Question</h4>
                <p className="text-sm text-gray-600">Use natural language to ask about your data</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                <span className="text-xs font-semibold text-blue-600">3</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Get Insights</h4>
                <p className="text-sm text-gray-600">Receive analysis, visualizations, and recommendations</p>
              </div>
            </div>
          </div>

          <Button 
            className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            onClick={() => handleStartConversation('data')}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Start with Analytics Agent
          </Button>
        </Card>
      </div>
    </div>
  );
}